import React, { FC, useEffect, useState } from 'react';
import { LandscapeProjectsAddProjectsImpactIndicatorsTemplate } from '../../templates';
import { AppDispatch, RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import LandscapeProjectNotSavedInfoPage from '../LandscapeProjectNotSavedInfoPage';
import Spinner from '../../atoms/Spinner/Spinner';
import { showError } from '../../../utils/Notifications';
import { IndicatorProjectSaveRequestDto } from '../../../store/landscapeAPI/indicator/dto/request/indicator-project-save-request.dto';
import { getUUID } from '../../../utils/UUID';
import {
  deleteIndicatorProject,
  saveIndicatorProject,
  updateIndicatorProject,
} from '../../../store/landscapeAPI/indicator/reducers/indicatorProject.slice';
import { LandscapeIndicatorDetailsResponseDto } from '../../../store/landscapeAPI/landscape/dto/response/landscape-indicator-details-response.dto';
import {
  deleteLandscapeIndicator,
  fetchLandscapeIndicatorDetailsByLandscapeId,
  fetchLandscapeIndicatorDetailsByProjectId,
} from '../../../store/landscapeAPI/landscape/reducers/landscape-indicator.slice';
import { TableItem } from '../../organisms/LandscapeProjectsAddProjectsImpactIndicatorsOrganism/LandscapeProjectsAddProjectsImpactIndicatorsOrganism';
import styles from './LandscapeProjectsAddProjectsImpactIndicatorsPage.module.css';
import cn from 'classnames';

interface LandscapeProjectsAddProjectsImpactIndicatorsPageProps {}

const LandscapeProjectsAddProjectsImpactIndicatorsPage: FC<
  LandscapeProjectsAddProjectsImpactIndicatorsPageProps
> = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { id, projectId } = useParams();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [data, setData] = useState<TableItem[]>();
  const [changeOrderEnabled, setChangeOrderEnabled] = useState(false);

  const {
    landscapeIndicatorsDetails,
    filteredLandscapeIndicatorsDetails,
    filteredStatus,
    status,
  } = useSelector((state: RootState) => state.landscapeIndicatorSlice);

  const [selectedIndicator, setSelectedIndicator] =
    useState<LandscapeIndicatorDetailsResponseDto>();

  useEffect(() => {
    if (!projectId || projectId === 'new-project') {
      return;
    }
    dispatch(fetchLandscapeIndicatorDetailsByLandscapeId(id!));
    dispatch(fetchLandscapeIndicatorDetailsByProjectId(projectId))
      .unwrap()
      .then((resp) => {
        const tableData = resp.map(
          (item: LandscapeIndicatorDetailsResponseDto) => ({
            name: item.name,
            type: item.type,
            sortOrder: item.sortOrder,
            id: item.id,
          })
        );
        setData(tableData);
      });
  }, [dispatch, projectId, id]);

  const onReorder = async (items: TableItem[], from: number, to: number) => {
    setData(items);
    let firstInd =
      filteredLandscapeIndicatorsDetails?.findIndex(
        (item) => item.sortOrder === from
      ) ?? -1;
    let secondInd =
      filteredLandscapeIndicatorsDetails?.findIndex(
        (item) => item.sortOrder === to
      ) ?? -1;
    if (
      firstInd !== -1 &&
      secondInd !== -1 &&
      filteredLandscapeIndicatorsDetails &&
      projectId
    ) {
      await dispatch(
        updateIndicatorProject({
          compactIndicatorId:
            filteredLandscapeIndicatorsDetails[firstInd].compactIndicatorid,
          projectId: projectId,
          sortOrder: to,
          id: filteredLandscapeIndicatorsDetails[firstInd].id,
          published: filteredLandscapeIndicatorsDetails[firstInd].published,
        })
      );
      await dispatch(
        updateIndicatorProject({
          compactIndicatorId:
            filteredLandscapeIndicatorsDetails[secondInd].compactIndicatorid,
          projectId: projectId,
          sortOrder: from,
          id: filteredLandscapeIndicatorsDetails[secondInd].id,
          published: filteredLandscapeIndicatorsDetails[secondInd].published,
        })
      );
    }
  };

  const handleDelete = async (id: string) => {
    await dispatch(deleteLandscapeIndicator(id)); //CHECK
    await dispatch(deleteIndicatorProject(id));
    if (projectId) {
      dispatch(fetchLandscapeIndicatorDetailsByProjectId(projectId))
        .unwrap()
        .then((resp) => {
          const tableData = resp.map(
            (item: LandscapeIndicatorDetailsResponseDto) => ({
              name: item.name,
              type: item.type,
              sortOrder: item.sortOrder,
              id: item.id,
            })
          );
          setData(tableData);
        });
    }
  };

  const handleSubmit = async () => {
    if (!selectedIndicator) {
      showError('Please select an indicator!');
      return;
    }

    const request: IndicatorProjectSaveRequestDto = {
      compactIndicatorId: selectedIndicator.compactIndicatorid,
      id: getUUID(),
      projectId: projectId!,
      published: true,
    };

    await dispatch(saveIndicatorProject(request));
    await dispatch(fetchLandscapeIndicatorDetailsByProjectId(projectId!));
    setShowModal(false);
  };

  if (status === 'loading' || filteredStatus === 'loading') {
    return <Spinner />;
  }

  return !projectId || projectId === 'new-project' ? (
    <LandscapeProjectNotSavedInfoPage />
  ) : (
    <div className={cn(styles.wrapper)}>
      <LandscapeProjectsAddProjectsImpactIndicatorsTemplate
        tableItems={data ?? []}
        setTableItems={onReorder}
        changeOrderEnabled={changeOrderEnabled}
        setChangeOrderEnabled={setChangeOrderEnabled}
        indicators={landscapeIndicatorsDetails ?? []}
        show={showModal}
        setShow={setShowModal}
        handleSubmit={handleSubmit}
        handleDelete={handleDelete}
        setSelectedIndicator={setSelectedIndicator}
      />
    </div>
  );
};

export default LandscapeProjectsAddProjectsImpactIndicatorsPage;
